/**
 * The Subskrypcja tab, as one projection: the entitlement joined with the seat read.
 *
 * ── This projection is still read-only, and since 21 September 2026 the tab is not ─────────
 * The web sells (requirement R13), but nothing about a LIVE subscription is decided here: what a
 * trainer may buy is `model/webOffers`, and where an existing subscription is managed is still the
 * channel that sold it (R4). `manage` names that place and hands over; it does not open anything.
 * Both reads arrive together — a card drawn from the first of two is a partial answer
 * wearing the shape of a complete one — and every seat number is `entities/client`'s projection,
 * never re-derived here.
 *
 * ── Five states this has to tell apart ──────────────────────────────────────────────────────
 * **free** (no renewal, no store, a bar — and nothing about where a plan is changed: the web sells
 * now, so the picker under this block IS that answer) · **capped paid** (renewal,
 * store, bar) · **unlimited** (no bar and no free-seat legend — `seatSegments` says why 999 cells is
 * a real risk) · **comped or an unknown channel** (a renewal if there is one and NO store link:
 * Apple's subscription list does not hold our subscription, so the link reads as broken) · **web**
 * (a renewal, „movebuddy.pl" as the place it was bought, and the provider's portal as the place it
 * is managed) · **a queued plan change** (one line above numbers that stay on today's plan) · **an
 * account paying in two channels at once** (a second line in the same place, saying so).
 *
 * ── Two facts that are easy to get wrong ────────────────────────────────────────────────────
 * · The renewal day is formatted with `timeZone: 'UTC'`. The store and the backend define the
 *   billing day in UTC, and local calendar fields shift it by one on a negative offset.
 * · The seat line comes from `activeClientLimit`, never a tier table. One seat is always the
 *   trainer's own (backend ADR 2026-08-12), so a cap of six is five clients plus you.
 *
 * Pure: `t` and the locale arrive as parameters.
 */

import type { TFunction } from 'i18next';

import { seatSegments, type SeatSegment, type SeatUsage } from '~/entities/client';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import {
  TrainWithMeDataEnumsSubscriptionChannel as Channel,
  type TrainWithMeModulesSubscriptionsDtosResponsesGetEntitlementsResponse as EntitlementsDto,
  type TrainWithMeModulesSubscriptionsDtosResponsesWebTrialResponse as WebTrialDto,
} from '~/shared/api/generated/model';

import { formatPrice } from './webOffers';

export type StoreTarget = 'appStore' | 'googlePlay';

/** The stores' own documented deep links. Neither carries an identifier. */
const STORE_URL: Readonly<Record<StoreTarget, string>> = {
  appStore: 'https://apps.apple.com/account/subscriptions',
  googlePlay: 'https://play.google.com/store/account/subscriptions',
};

/** Exported because the erase dialog's notice needs the same address from a bare target. */
export function storeUrlFor(target: StoreTarget): string {
  return STORE_URL[target];
}

/**
 * Which store manages this plan, if any. The store follows the PURCHASE, not the device: a
 * subscription bought on an iPhone is managed at Apple however it is read. A browser is no
 * storefront, so `TestStore`, `Comp`, `Web` and an absent channel all answer `null` — a web
 * purchase is managed in the provider's portal, which `SubscriptionCard.manage` names instead.
 */
export function storeTargetOf(entitlements: EntitlementsDto): StoreTarget | null {
  if (entitlements.isFree === true) return null;
  switch (entitlements.channel) {
    case Channel.AppStore:
      return 'appStore';
    case Channel.GooglePlay:
      return 'googlePlay';
    default:
      return null;
  }
}

/**
 * A channel's own name, as the trainer would recognise it on a bank statement: the two storefronts
 * and this site. A channel with no name of its own — `Comp`, `TestStore` — answers `null` and drops
 * out of the sentence rather than being printed raw.
 */
export function channelName(channel: string, t: TFunction): string | null {
  switch (channel) {
    case Channel.AppStore:
      return t('web:account.subscription.store.appStore');
    case Channel.GooglePlay:
      return t('web:account.subscription.store.googlePlay');
    case Channel.Web:
      return t('web:account.subscription.store.web');
    default:
      return null;
  }
}

export interface SeatLegendEntry {
  readonly kind: SeatSegment;
  readonly label: string;
}

export interface SubscriptionStore {
  readonly target: StoreTarget;
  readonly url: string;
  /** „App Store" / „Google Play" — the store's own name, never translated. */
  readonly name: string;
}

export interface SubscriptionCard {
  readonly planName: string;
  /**
   * The „Plan" row's value: the name, and on an unlimited plan what makes it that plan — „Pro · bez
   * limitu klientów". A capped plan says only its name, because the row under it carries the cap.
   */
  readonly planLine: string;
  /** True on the free plan — no renewal, no store, and no crown on the plate. */
  readonly isFree: boolean;
  /** The big tabular number: seats spoken for, or — on an unlimited plan — people coached. */
  readonly seatFigure: number;
  /** „/ 6 miejsc klientów", or the sentence that replaces it when there is no cap. */
  readonly seatCaption: string;
  /** Empty exactly when no bar is drawn. */
  readonly segments: readonly SeatSegment[];
  /** Empty with the bar. The free entry is dropped on an unlimited plan — there is nothing free. */
  readonly legend: readonly SeatLegendEntry[];
  /** The whole bar as one sentence, because a row of coloured cells is not a reading. */
  readonly seatsA11y: string;
  /** „Odnowienie" — absent on the free plan and whenever the backend sent no period end. */
  readonly renewal: string | null;
  /**
   * The title the renewal date stands under. A subscription cancelled at the period end does not
   * renew on that date, it ends — and „Odnowienie" above it would promise a charge that is not coming.
   */
  readonly renewalTitle: string;
  /**
   * The line under the renewal title — today only a running trial's „cancel before … and nothing is
   * charged". `null` everywhere else, including a trial already cancelled: there is nothing left to
   * cancel.
   */
  readonly renewalNote: string | null;
  /** The store this was bought in, or `null` when no storefront manages it. */
  readonly store: SubscriptionStore | null;
  /**
   * The „Kupiona w" value: a store's own name, `movebuddy.pl` for a web purchase, or `null` when
   * nobody sold this — a comped subscription and the free plan alike.
   */
  readonly boughtIn: string | null;
  /**
   * WHERE this subscription is managed, which requirement R4 makes exclusive to the channel that
   * sold it. `store` is a link that leaves the browser; `portal` is a button, because the payment
   * provider's portal address is minted per session by the backend and cannot be written into an
   * `href`. `null` is the honest third answer: a comped subscription is managed by nobody.
   */
  readonly manage: 'store' | 'portal' | null;
  /** The plate's own line: „5 klientów + Ty", „Bez limitu klientów", or the honest one-seat wording. */
  readonly plateSeats: string;
  /** A queued end-of-period change, as one line above today's numbers. */
  readonly pendingChange: string | null;
  /**
   * „You are also paying in App Store" — the warning that this account is billed twice.
   *
   * `null` is the ordinary case. The backend decides WHICH channels count as a parallel paid one
   * (a comped grant is not a payment and never appears here); this only words the answer.
   */
  readonly parallelBilling: string | null;
}

export interface SubscriptionCardInput {
  readonly entitlements: EntitlementsDto;
  readonly seats: SeatUsage;
  /**
   * `GET /me/subscription/web/trial`, read for a web subscription only — or `undefined` when it was
   * not read, failed, or has not answered. Requirement R18: the tab shows the plain renewal when the
   * provider does not answer, so every absence here is the ordinary renewal row, silently.
   */
  readonly trial?: WebTrialDto | null | undefined;
  readonly locale: string;
  readonly t: TFunction;
}

/** A date the store defined in UTC, as this locale writes it. `null` for anything unreadable. */
function formatUtcDate(iso: string | null | undefined, locale: string): string | null {
  if (iso == null || iso === '') return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return dateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(at);
}

/** The plate's seat line, off the cap alone — see the header for the „+ Ty". */
function plateSeatLine(limit: number | null, t: TFunction): string {
  if (limit === null) return t('web:account.subscription.plateUnlimited');
  if (limit <= 1) return t('web:account.subscription.plateSelfOnly');
  return t('web:account.subscription.plateSeats', { count: limit - 1 });
}

function legendOf(seats: SeatUsage, t: TFunction): readonly SeatLegendEntry[] {
  return [
    { kind: 'active' as const, label: t('profile.hub.subscription.legendActive', { count: seats.active }) },
    { kind: 'pending' as const, label: t('profile.hub.subscription.legendPending', { count: seats.pending }) },
    { kind: 'free' as const, label: t('profile.hub.subscription.legendFree', { count: seats.free ?? 0 }) },
  ];
}

/** What the renewal row says, as one decision: a running trial, or the ordinary renewal. */
interface RenewalLine {
  readonly title: string;
  readonly value: string | null;
  readonly note: string | null;
}

/**
 * The renewal row during a web trial (R19): free until the trial's end, and what follows it — the
 * year's charge, or the free plan once the trial was cancelled. `null` when there is no trial to
 * speak of, which hands the row back to the ordinary renewal.
 *
 * A trial whose charge did not come back still has an end the trainer is owed; it loses only the
 * „then …" half of the sentence, never the date.
 */
function trialLine(
  trial: WebTrialDto | null | undefined,
  cancelled: boolean,
  locale: string,
  t: TFunction,
): RenewalLine | null {
  const date = formatUtcDate(trial?.trialEndsAt, locale);
  if (date === null) return null;
  const charge = trial?.chargeAtTrialEnd;
  const price =
    typeof charge?.amountMinor === 'number' && typeof charge.currency === 'string' && charge.currency !== ''
      ? formatPrice(charge.amountMinor, charge.currency)
      : null;
  return {
    title: t('web:account.subscription.trial.rowTitle'),
    value: cancelled
      ? t('web:account.subscription.trial.rowThenFree', { date })
      : price === null
        ? t('web:account.subscription.trial.rowFreeUntil', { date })
        : t('web:account.subscription.trial.rowThenCharge', { date, price }),
    note: cancelled ? null : t('web:account.subscription.trial.rowNote', { date }),
  };
}

export function subscriptionCard({
  entitlements,
  seats,
  trial,
  locale,
  t,
}: SubscriptionCardInput): SubscriptionCard {
  const isFree = entitlements.isFree === true;
  const limit = entitlements.plan?.activeClientLimit ?? null;
  const segments = seatSegments(seats);
  const target = storeTargetOf(entitlements);
  // A web subscription is the one non-store channel this app can actually act on: the provider's
  // portal does every change R8 allows, and the tab's own button is the way into it.
  const isWeb = !isFree && entitlements.channel === Channel.Web;
  const store =
    target === null
      ? null
      : {
          target,
          url: storeUrlFor(target),
          name: t(`web:account.subscription.store.${target}`),
        };

  // Only an explicit `false` is a cancellation: an absent flag is a channel that does not report it
  // (a comped grant), and that one keeps the neutral title.
  const cancelled = !isFree && entitlements.autoRenew === false;
  const renewal: RenewalLine = (isWeb ? trialLine(trial, cancelled, locale, t) : null) ?? {
    title: cancelled ? t('web:account.subscription.ends') : t('web:account.subscription.renewal'),
    value: isFree ? null : formatUtcDate(entitlements.currentPeriodEnd, locale),
    note: null,
  };

  const pendingAt = formatUtcDate(entitlements.pendingPlanChange?.effectiveAt, locale);
  const pendingName = entitlements.pendingPlanChange?.planName ?? '';

  /* Every channel that is billing this account BESIDES the one it is entitled through. Two of them
   * is a real shape — an App Store subscription and a Google Play one — so they are joined by the
   * locale's own conjunction rather than by a comma written into the copy. `Intl.ListFormat` is
   * built once per projection, which is why it is not in `shared/lib/intlCache`: that cache is for
   * formatters built per row. */
  const parallel = (entitlements.parallelPaidChannels ?? [])
    .map((channel) => channelName(channel, t))
    .filter((name): name is string => name !== null);

  return {
    planName: entitlements.plan?.name ?? '',
    planLine:
      seats.unlimited
        ? t('web:account.subscription.planLineUnlimited', { plan: entitlements.plan?.name ?? '' })
        : (entitlements.plan?.name ?? ''),
    isFree,
    seatFigure: seats.taken,
    seatCaption: seats.unlimited
      ? t('web:account.subscription.seatsUnlimited')
      : t('profile.hub.subscription.seats', { count: seats.cap ?? 0 }),
    segments,
    legend: segments.length === 0 ? [] : legendOf(seats, t),
    seatsA11y: seats.unlimited
      ? t('web:account.subscription.seatA11yUnlimited', { used: seats.taken })
      : t('profile.hub.subscription.seatA11y', { used: seats.taken, total: seats.cap ?? 0 }),
    renewal: renewal.value,
    renewalTitle: renewal.title,
    renewalNote: renewal.note,
    store,
    boughtIn: store?.name ?? (isWeb ? t('web:account.subscription.store.web') : null),
    manage: store !== null ? 'store' : isWeb ? 'portal' : null,
    plateSeats: plateSeatLine(limit, t),
    // A change with no readable date has nothing to announce — „od  plan Starter" is worse than
    // silence — so it collapses rather than rendering a gap.
    pendingChange:
      pendingAt === null
        ? null
        : t('profile.hub.subscription.pendingChange', { date: pendingAt, plan: pendingName }),
    parallelBilling:
      parallel.length === 0
        ? null
        : t('web:account.subscription.parallelBilling', {
            channel: new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
              parallel,
            ),
          }),
  };
}
